'use client';

import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { useProfileModalOptional } from '@/contexts/profile-modal-context';
import type { MockBoard } from '../data/mock-boards';
import { BoardCover, boardInitials } from '../ui/BoardCover';
import { XIcon, TelegramIcon, GlobeIcon } from '../ui/BrandIcons';
import { useExternalLinkGuard } from '../ui/ExternalLinkGuard';
import { BOARDROOM_BOARD_ID } from '../data/boardroom-grid';
/** Live or fallback identity for the board creator — same shape as the pinned title card. */
export interface BoardCreatorPin {
  address: string;
  displayName: string;
  avatarUrl?: string | null;
  level: number;
}

/** Matches `PinnedTitlePanel` glass shell — shared by the pinned card and board hover tooltip. */
export const BOARD_METADATA_GLASS =
  'bg-[rgba(6,5,8,0.92)] backdrop-blur-xl backdrop-saturate-150 border-amber-500/35 ' +
  'shadow-[inset_0_0_0_1px_rgba(255,215,120,0.12),0_22px_50px_-14px_rgba(0,0,0,0.88)]';

function formatCreatorLabel(name: string): string {
  const t = name.trim();
  if (t.startsWith('@')) return t;
  if (/[.]eth$/i.test(t)) return t;
  // Shortened on-chain addresses (`0x028f…6949`) are surfaced when the
  // creator hasn't linked an X / ENS / Farcaster identity (e.g. the
  // launch-metadata POST never landed). Don't slap an `@` on them —
  // it would imply a handle that doesn't exist.
  if (/^0x[0-9a-f]{4,6}[….]/i.test(t)) return t;
  return `@${t}`;
}

/**
 * Format a Harberger tax rate in basis points as a per-week percent
 * string, e.g. `500n` → `"5%/wk tax"`. Trims trailing zeros so a clean
 * `1000` (10%) renders as `10%/wk` rather than `10.0%/wk`. Returns
 * `null` for missing / non-positive values so the caller can omit the
 * chip entirely on mock boards.
 */
function formatTaxRateBps(taxRateBps: number | undefined): string | null {
  if (typeof taxRateBps !== 'number' || taxRateBps <= 0) return null;
  const pct = taxRateBps / 100;
  const text = Number.isInteger(pct) ? pct.toFixed(0) : pct.toFixed(2).replace(/\.?0+$/u, '');
  return `${text}%/wk tax`;
}

export function BoardMetadataSnapshot({
  board,
  creator = null,
  headerTrailing = null,
  footer = null,
  className,
  loading = false,
}: {
  board: MockBoard;
  creator?: BoardCreatorPin | null;
  /** e.g. hide button on the pinned title card */
  headerTrailing?: ReactNode;
  /** Optional creator-only Edit Panel rendered inside the same rounded
   *  glass shell, after the socials row — mirrors the `footer` slot on
   *  `AdapterStack` so the Info card and Board Yield card share the
   *  same nested-edit pattern. */
  footer?: ReactNode;
  className?: string;
  /** While true, preserve the static board name as-is but swap the dynamic
   *  creator pill + description + socials for skeleton bars so live boards
   *  never flash `MOCK_BOARDS[0]` copy between navigations. */
  loading?: boolean;
}) {
  const profileModal = useProfileModalOptional();
  const taxRateLabel = formatTaxRateBps(board.taxRateBps);
  // Forfeit-mode chip — answers "what happens if my deposit runs out?"
  // at a glance. v2.2 grids route the seat into a Dutch auction at the
  // holder's last self-assessed price, decaying to zero over
  // `forfeitureDuration`; v2.1 grids drop the seat into instant
  // free-claim. The FAQ's "What happens at forfeit?" exhibit goes
  // deeper — we just surface the headline behaviour here so a viewer
  // can see *which mode* a given board is on without opening the dialog.
  const forfeitMode: 'dutch' | 'instant' | null =
    board.gridVersion === 'v2.2'
      ? 'dutch'
      : board.gridVersion === 'v2.1'
        ? 'instant'
        : null;
  // The Boardroom is a protocol-authored board (HV grid 0 — see
  // `useLobbyBoards.brandBoardroomOverlay`), and its socials point at
  // first-party destinations baked into the canonical `MockBoard`. The
  // `ExternalLinkGuard` interstitial exists to slow users down on
  // user-/protocol-authored metadata we can't vet — for our own links
  // it's friction without a corresponding safety win, so we skip the
  // modal entirely for Boardroom rows. Every other board still routes
  // through the guard (trusted-host fast path for X/Twitter, full DYOR
  // modal for everything else).
  const skipMetadataGuard = board.id === BOARDROOM_BOARD_ID;

  return (
    <div
      className={cn(
        'relative flex flex-col w-[296px] max-w-[96vw] rounded-[5px] border overflow-hidden',
        BOARD_METADATA_GLASS,
        className,
      )}
      aria-busy={loading || undefined}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-500/15">
        <BoardCover
          seed={board.id}
          hue={board.hue}
          initials={boardInitials(board.name)}
          imageUrl={board.imageUrl ?? null}
          size={32}
        />
        <div className="flex-1 min-w-0 leading-tight">
          <div className="text-[12px] font-semibold text-amber-100 tracking-wide truncate leading-tight">
            {board.name}
          </div>
          <div className="min-w-0 flex flex-col gap-0">
            <div className="text-[9px] font-mono tracking-[0.18em] uppercase text-zinc-400 truncate leading-none flex items-center gap-1.5">
              <span>{board.gridSize}×{board.gridSize}</span>
              {taxRateLabel && (
                <>
                  <span aria-hidden className="text-amber-500/40">·</span>
                  <span className="text-amber-200/80">{taxRateLabel}</span>
                </>
              )}
              {forfeitMode && (
                <>
                  <span aria-hidden className="text-amber-500/40">·</span>
                  <span
                    className={cn(
                      forfeitMode === 'dutch'
                        ? 'text-amber-200/80'
                        : 'text-zinc-300/80',
                    )}
                    title={
                      forfeitMode === 'dutch'
                        ? 'v2.2 board · forfeit goes to a Dutch auction at your last price, decaying to 0 over the grid\u2019s forfeiture window'
                        : 'v2.1 board · forfeit goes straight to instant free-claim by the next caller'
                    }
                  >
                    {forfeitMode === 'dutch' ? 'Dutch forfeit' : 'Instant claim'}
                  </span>
                </>
              )}
            </div>
            {loading ? (
              <div className="mt-1 h-2 w-[40%] rounded bg-amber-500/10 animate-pulse" />
            ) : creator ? (
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => profileModal?.openProfile(creator.address)}
                  className="m-0 p-0 h-auto block w-full text-left text-[10px] font-medium leading-tight text-zinc-200 hover:text-amber-100 truncate max-w-full underline-offset-2 hover:underline"
                >
                  {formatCreatorLabel(creator.displayName)}
                </button>
              </div>
            ) : null}
          </div>
        </div>
        <div className="shrink-0 flex items-start gap-1">
          {headerTrailing}
        </div>
      </div>

      <div
        className={cn(
          'px-3 py-2 text-[11px] leading-snug border-b border-amber-500/10',
          'text-zinc-100 [text-shadow:0_1px_2px_rgba(0,0,0,0.65)]',
        )}
      >
        {loading ? (
          <div className="flex flex-col gap-1.5">
            <div className="h-2 w-full rounded bg-amber-500/10 animate-pulse" />
            <div className="h-2 w-[85%] rounded bg-amber-500/10 animate-pulse" />
          </div>
        ) : (
          board.description
        )}
      </div>

      <div className="px-3 py-2 flex items-center gap-1.5 flex-wrap min-h-[28px]">
        {loading ? (
          <>
            <div className="h-5 w-10 rounded-[2px] bg-amber-500/10 animate-pulse" />
            <div className="h-5 w-14 rounded-[2px] bg-amber-500/10 animate-pulse" />
          </>
        ) : (
          <>
            {board.socials.x && (
              <SocialLink
                href={board.socials.x}
                label="X"
                icon={<XIcon size={11} />}
                useMetadataGuard
                skipGuard={skipMetadataGuard}
              />
            )}
            {board.socials.website && (
              <SocialLink
                href={board.socials.website}
                label="Site"
                icon={<GlobeIcon size={11} />}
                useMetadataGuard
                skipGuard={skipMetadataGuard}
              />
            )}
            {board.socials.telegram && (
              <SocialLink
                href={board.socials.telegram}
                label="Telegram"
                icon={<TelegramIcon size={11} />}
                skipGuard={skipMetadataGuard}
              />
            )}
            {board.socials.discord && (
              <SocialLink
                href={board.socials.discord}
                label="Discord"
                icon={<GlobeIcon size={11} />}
                skipGuard={skipMetadataGuard}
              />
            )}
          </>
        )}
      </div>

      {footer ? (
        <div className="px-2 pb-2 pt-0 border-t border-amber-500/15">
          <div className="pt-2">{footer}</div>
        </div>
      ) : null}
    </div>
  );
}

function SocialLink({
  href,
  label,
  icon,
  useMetadataGuard,
  skipGuard,
}: {
  href: string;
  label: string;
  icon: React.ReactNode;
  /** Board “Site” and similar — always show the external-link interstitial (even for trusted hosts). */
  useMetadataGuard?: boolean;
  /**
   * Bypass the external-link guard entirely and let the browser
   * follow the anchor's `href` natively. Used for protocol-authored
   * boards (the Boardroom) whose socials are first-party and don't
   * need the DYOR interstitial.
   */
  skipGuard?: boolean;
}) {
  const { guardClick, guardMetadataClick } = useExternalLinkGuard();
  const onClick = skipGuard
    ? undefined
    : useMetadataGuard
      ? guardMetadataClick(href)
      : guardClick(href);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="inline-flex items-center gap-1 h-5 px-1.5 rounded-[2px] text-[9px] font-mono tracking-[0.18em] uppercase bg-black/40 border border-white/10 text-zinc-300 hover:text-amber-100 hover:border-amber-400/40 transition-colors"
    >
      <span className="text-amber-200/90">{icon}</span>
      {label}
    </a>
  );
}
